import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCase } from '@/lib/api';
import { motion } from 'framer-motion';
import { fadeUp } from '@/lib/motion';
import { PageHero, Section, SectionTitle } from '@/components/shared/Section';
import { MiniForm } from '@/components/shared/Forms';
import { ShieldCheck, ArrowRight, MessageCircle, Quote, TrendingUp, CheckCircle } from 'lucide-react';

export default function CaseDetailPage() {
  const { slug } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getCase(slug).then(r => { setCaseStudy(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0EA5E9] border-t-transparent rounded-full animate-spin" /></div>;
  if (!caseStudy) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caso no encontrado</div>;

  return (
    <div>
      <PageHero
        badge={`CASO DE ÉXITO · ${caseStudy.industry?.toUpperCase()}`}
        badgeIcon={TrendingUp}
        h1={caseStudy.title}
        subtitle={caseStudy.summary}
        primaryCTA={<Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg">Quiero resultados similares <ArrowRight className="w-4 h-4" /></Link>}
        secondaryCTA={<a href="https://wa.me/56975294340" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-emerald-400 border border-emerald-400/30 rounded-xl hover:bg-emerald-400/10"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
      />

      {/* Metrics */}
      {caseStudy.metrics && caseStudy.metrics.length > 0 && (
        <Section alt>
          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {caseStudy.metrics.map((m, i) => (
              <motion.div key={i} variants={fadeUp} className="text-center p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
                <div className="text-2xl sm:text-3xl font-bold text-[#0A2540] mb-1" style={{fontFamily:'Syne,sans-serif'}}>{m.value}</div>
                <div className="text-sm text-[#64748B]">{m.label}</div>
              </motion.div>
            ))}
          </div>
        </Section>
      )}

      {/* Problem & Solution */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div variants={fadeUp} className="p-6 sm:p-8 rounded-2xl bg-red-50 border border-red-100">
            <h3 className="text-lg font-semibold mb-3 text-red-900" style={{fontFamily:'Syne,sans-serif'}}>El desafío</h3>
            <p className="text-sm text-red-800 leading-relaxed">{caseStudy.problem}</p>
          </motion.div>
          <motion.div variants={fadeUp} className="p-6 sm:p-8 rounded-2xl bg-emerald-50 border border-emerald-100">
            <h3 className="text-lg font-semibold mb-3 text-emerald-900" style={{fontFamily:'Syne,sans-serif'}}>La solución ALLCOM</h3>
            <p className="text-sm text-emerald-800 leading-relaxed">{caseStudy.solution}</p>
          </motion.div>
        </div>
      </Section>

      {/* Products Used */}
      {caseStudy.products_used && caseStudy.products_used.length > 0 && (
        <Section alt>
          <SectionTitle badge="TECNOLOGÍA UTILIZADA" title="Productos y servicios implementados" center />
          <div className="flex flex-wrap justify-center gap-3">
            {caseStudy.products_used.map((p, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 shadow-sm">
                <CheckCircle className="w-4 h-4 text-[#0EA5E9]" />
                <span className="text-sm font-medium text-slate-700">{p}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Quote */}
      {caseStudy.quote && (
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <Quote className="w-10 h-10 text-[#0EA5E9] mx-auto mb-4" />
            <blockquote className="text-xl sm:text-2xl font-medium text-[#0A2540] leading-relaxed mb-4" style={{fontFamily:'Syne,sans-serif'}}>
              "{caseStudy.quote}"
            </blockquote>
            <div className="text-sm text-[#64748B]">— Cliente {caseStudy.industry}</div>
          </div>
        </Section>
      )}

      {/* CTA */}
      <Section alt>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTitle badge="TU TURNO" title="¿Quieres resultados similares?" subtitle="Conversemos sobre cómo ALLCOM puede transformar tu operación con tecnología en terreno." />
          </div>
          <div className="p-6 sm:p-8 rounded-2xl bg-white border border-slate-200 shadow-lg">
            <MiniForm sourcePage={`/casos/${slug}`} />
          </div>
        </div>
      </Section>

      {/* Back */}
      <Section>
        <div className="text-center">
          <Link to="/casos" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0EA5E9] hover:underline">
            ← Ver todos los casos
          </Link>
        </div>
      </Section>
    </div>
  );
}
